import '@/theme/styles.css';
import { initTimeZone } from '@groupthinkai/groupthink';
import { CssBaseline } from '@mui/joy';
import { CssVarsProvider } from '@mui/joy/styles';
import { useMediaQuery } from '@mui/material';
import {
  THEME_ID as MATERIAL_THEME_ID,
  Experimental_CssVarsProvider as MaterialCssVarsProvider,
  experimental_extendTheme as materialExtendTheme,
} from '@mui/material/styles';
import { LicenseInfo } from '@mui/x-license';
import Head from 'next/head';
import { useRouter } from 'next/router';
import Script from 'next/script';
import queryString from 'query-string';
// import { scan } from 'react-scan'; // Must be imported before React
import React, { useState } from 'react';

import { DesktopEventHandler } from '@/components/DesktopEventHandler';
import { TokenProvider } from '@/components/TokenContext';
import { NotificationSnackbarProvider } from '@/hooks/notifications';
import { LoadingProvider } from '@/layouts/LoadingProvider';
import theme, { bitter, publicSans, redHatDisplay, shouldSkipGeneratingVar } from '@/theme/theme';
initTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone);
/**
 * !!!IMPORTANT!!!
 *
 * If you add a new third party script source to this file, you must also add it to the Content Security Policy in the apps/electron/main.js file.
 */

LicenseInfo.setLicenseKey(process.env.NEXT_PUBLIC_MUI_LICENSE_KEY || '');

// Configure react-scan when running in the browser
// Uncomment to use react-scan during development
// if (typeof window !== 'undefined') {
//   scan({
//     enabled: process.env.NEXT_PUBLIC_REACT_SCAN_OVERLAY === 'true',
//     log: process.env.NEXT_PUBLIC_REACT_SCAN_VERBOSE_LOGS === 'true',
//   });
// }

export default function App({ Component, pageProps }) {
  const [mounted, setMounted] = useState(false);
  const router = useRouter();
  const { chromeless } = router.query;

  const isChromeless = chromeless === '1';
  const getLayout = Component.getLayout || ((page) => page);

  const tag_manager_id = process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_GTAG_ID;
  const development_tag_manager_id = process.env.NEXT_PUBLIC_DEVELOPMENT_GOOGLE_TAG_MANAGER_GTAG_ID;
  const mdPlus = useMediaQuery(theme.breakpoints.up('md'));
  const materialTheme = materialExtendTheme(
    {
      palette: {
        mode: 'light',
      },
    },
    []
  );

  React.useEffect(() => {
    setMounted(true);
  }, []);

  React.useEffect(() => {
    const handleRouteChange = (url) => {
      if (window.gtag) {
        // Include platform info in tracking payload
        let payload = {
          page_location: url,
          platform: process.env.NEXT_PUBLIC_PLATFORM || 'web', // Send platform info (web or electron)
        };

        window.gtag('event', 'page_view', payload);
      }
    };

    // Send page_view on initial load
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_location: router.asPath,
        platform: process.env.NEXT_PUBLIC_PLATFORM || 'web',
      });
    }

    router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events, router.asPath]);

  // Derive the app argument string from the current path and query string, and display "Open in App" toolbar on iOS devices
  const currentPath = router.asPath.split('?')[0];
  const queryStr = queryString.stringify(router.query);
  const appArgumentStr = `groupthink://${currentPath.substr(1)}${
    queryStr.length > 0 ? '?' + queryStr : ''
  }`;

  let viewportContent = {
    width: 'device-width',
    'initial-scale': '1.0',
    'maximum-scale': mdPlus && !isChromeless ? '5.0' : '1.0',
    'user-scalable': mdPlus && !isChromeless ? '1' : '0',
    'viewport-fit': mdPlus ? 'cover' : 'auto', // Use full screen on larger devices
  };

  const metaViewportContent = React.useMemo(
    () =>
      Object.entries(viewportContent)
        .map(([key, value]) => `${key}=${value}`)
        .join(', '),
    [viewportContent]
  );

  if (process.env.NEXT_PUBLIC_PLATFORM === 'electron' && !mounted) {
    // Prevents hydration error in Electron
    return <div style={{ visibility: 'hidden' }}>Loading...</div>;
  }

  return (
    <>
      <Head>
        <link rel="shortcut icon" href="/gt_primary.svg" type="image/svg+xml" key="favicon" />
        <meta name="mobile-web-app-capable" content="yes"></meta>

        <meta
          name="apple-itunes-app"
          content={`app-id=6446509018, app-argument=${appArgumentStr}`}></meta>
        <meta name="viewport" content={metaViewportContent} />
      </Head>
      <Script
        dangerouslySetInnerHTML={{
          __html: `
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          platform: '${process.env.NEXT_PUBLIC_PLATFORM || 'web'}',
          isElectron: ${process.env.NEXT_PUBLIC_PLATFORM === 'electron'}
        });
      `,
        }}
      />
      {tag_manager_id && (
        <>
          <Script
            dangerouslySetInnerHTML={{
              __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://metrics.groupthink.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','${tag_manager_id}');`,
            }}
          />
          <noscript
            dangerouslySetInnerHTML={{
              __html: `<iframe
                src="https://metrics.groupthink.com/ns.html?id=${tag_manager_id}"
                height="0"
                width="0"
                style="display: none;
                visibility: hidden;"
                ></iframe>`,
            }}
          />
        </>
      )}
      {development_tag_manager_id && (
        <>
          <Script
            dangerouslySetInnerHTML={{
              __html: `
              window.dataLayer = window.dataLayer || [];
              window.dataLayer.push({
                platform: '${process.env.NEXT_PUBLIC_PLATFORM || 'web'}',
                isElectron: ${process.env.NEXT_PUBLIC_PLATFORM === 'electron'}
              });
            `,
            }}
          />
          <Script
            dangerouslySetInnerHTML={{
              __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
              new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
              j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
              'https://www.googletagmanager.com/gtm.js?id='+i+dl+
              '&gtm_auth=${process.env.NEXT_PUBLIC_DEVELOPMENT_GOOGLE_TAG_MANAGER_AUTH}&gtm_preview=${process.env.NEXT_PUBLIC_DEVELOPMENT_GOOGLE_TAG_MANAGER_ENV_ID}&gtm_cookies_win=x';f.parentNode.insertBefore(j,f);
              })(window,document,'script','dataLayer','${development_tag_manager_id}');`,
            }}
          />
          <noscript
            dangerouslySetInnerHTML={{
              __html: `<iframe
                src="https://www.googletagmanager.com/ns.html?id=${development_tag_manager_id}&gtm_auth=${process.env.NEXT_PUBLIC_DEVELOPMENT_GOOGLE_TAG_MANAGER_AUTH}&gtm_preview=${process.env.NEXT_PUBLIC_DEVELOPMENT_GOOGLE_TAG_MANAGER_ENV_ID}&gtm_cookies_win=x"
                height="0"
                width="0"
                style="display: none;
                visibility: hidden;"
                ></iframe>`,
            }}
          />
        </>
      )}

      <MaterialCssVarsProvider theme={{ [MATERIAL_THEME_ID]: materialTheme }}>
        <CssVarsProvider theme={theme} shouldSkipGeneratingVar={shouldSkipGeneratingVar}>
          <CssBaseline />
          <TokenProvider onTokenRetrieval={() => router.push('/dashboard')}>
            <NotificationSnackbarProvider>
              <DesktopEventHandler>
                <LoadingProvider>{getLayout(<Component {...pageProps} />)}</LoadingProvider>
              </DesktopEventHandler>
            </NotificationSnackbarProvider>
          </TokenProvider>
        </CssVarsProvider>
      </MaterialCssVarsProvider>
    </>
  );
}
